import { getCaptchaToken } from "@/features/utils/captcha";
import { useTranslation } from "@/features/utils/i18n";
import { useControlledEffect } from "@/features/utils/use-controlled-effect";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { AuthAPI } from "../api/auth";
import { userKeys } from "./use-user";

export function useAddSecondaryPhone() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean },
    Error,
    { phoneChallengeToken: string }
  >({
    mutationFn: AuthAPI.addSecondaryPhone,
    onSuccess: () => {
      // Invalidate user queries to refresh the user data
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success(t("security.messages.secondaryPhoneAdded"));
    },
  });
}

export function useRemoveSecondaryPhone() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, { phone: string }>({
    mutationFn: AuthAPI.removeSecondaryPhone,
    onSuccess: () => {
      // Invalidate user queries to refresh the user data
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success(t("security.messages.secondaryPhoneRemoved"));
    },
  });
}

export function useMakePrimaryPhone() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, { phone: string }>({
    mutationFn: AuthAPI.makePrimaryPhone,
    onSuccess: () => {
      // Invalidate user queries to refresh the user data
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success(t("security.messages.primaryPhoneUpdated"));
    },
  });
}

export function usePhoneChallengeFlow(
  options: {
    enforcedValue?: {
      label: string; // Ex: "+33 6 ** ** ** 42"
      id: string; // Used by the backend to find the correct phone
    };
    /** Pre-fills the number field; nothing is sent until the user asks. */
    initialPhone?: string;
    onSuccess?: (preSignedToken: string) => void;
    retryDelaySeconds?: number; // Default: 60 seconds
  } = {}
) {
  const {
    enforcedValue,
    initialPhone,
    onSuccess,
    retryDelaySeconds = 60,
  } = options;
  const { t } = useTranslation();

  // Raw hooks using AuthAPI methods directly
  const sendChallengeRaw = useMutation({
    mutationFn: AuthAPI.phoneChallenge,
  });
  const verifyRaw = useMutation({
    mutationFn: AuthAPI.phoneVerify,
  });

  // State
  const [phone, setPhone] = useState(initialPhone || "");
  const [codeSent, setCodeSent] = useState(enforcedValue?.id ? true : false);
  const [retryTimeLeft, setRetryTimeLeft] = useState(0);

  // Start retry timer
  const startRetryTimer = useCallback(() => {
    setRetryTimeLeft(retryDelaySeconds);
    const interval = setInterval(() => {
      setRetryTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [retryDelaySeconds]);

  // Send challenge
  const sendChallenge = useCallback(async () => {
    try {
      const phoneToUse = enforcedValue?.id || phone;
      const result = await sendChallengeRaw.mutateAsync({
        phone: phoneToUse,
        captchaToken: await getCaptchaToken("phone_challenge"),
      });

      if (!result.success) {
        throw new Error(result.message || t("security.messages.challengeFailed"));
      }
      setCodeSent(true);
      startRetryTimer();
      toast.success(t("security.messages.codeSentPhone"));
    } catch (error) {
      console.error(error);
      // The server's reason is English and meant for the logs.
      toast.error(t("security.messages.challengeFailed"));
      throw error;
    }
  }, [phone, enforcedValue, sendChallengeRaw, startRetryTimer, t]);

  // Verify code
  const verifyCode = useCallback(
    async (otp: string) => {
      try {
        const phoneToUse = enforcedValue?.id || phone;
        const result = await verifyRaw.mutateAsync({
          phone: phoneToUse,
          otp,
        });

        if (result.success) {
          toast.success(t("security.messages.phoneVerified"));
          onSuccess?.(result.preSignedToken);
        } else {
          throw new Error(result.message || t("security.messages.verifyFailed"));
        }
      } catch (error) {
        console.error(error);
        toast.error(t("security.messages.verifyFailed"));
        throw error;
      }
    },
    [phone, enforcedValue, verifyRaw, onSuccess, t]
  );

  // Retry (go back to phone input if not enforced, or resend if enforced)
  const retry = useCallback(() => {
    if (enforcedValue) {
      // If enforced, just resend the challenge
      sendChallenge();
    } else {
      // If not enforced, go back to phone input
      setCodeSent(false);
      setRetryTimeLeft(0);
    }
  }, [enforcedValue, sendChallenge]);

  // Auto-send challenge if enforced value is provided
  useControlledEffect(() => {
    if (enforcedValue?.id) {
      sendChallenge();
    }
  }, [enforcedValue?.id]);

  return {
    phone,
    setPhone,
    sendChallenge,
    codeSent,
    verifyCode,
    isVerifyingCode: verifyRaw.isPending,
    retryTimeLeft,
    canRetry: retryTimeLeft === 0,
    retry,
  };
}
